import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { MapPin, CheckCircle, ChevronRight } from "lucide-react";

const AARHUS = [56.1629, 10.2039];
const TOWNS = [
  { name: "Aarhus", pos: AARHUS, main: true },
  { name: "Randers", pos: [56.4607, 10.0364] },
  { name: "Silkeborg", pos: [56.1697, 9.5451] },
  { name: "Horsens", pos: [55.8607, 9.8503] },
  { name: "Skanderborg", pos: [56.0333, 9.9333] },
  { name: "Grenaa", pos: [56.4131, 10.8875] },
  { name: "Ebeltoft", pos: [56.1944, 10.6822] },
  { name: "Odder", pos: [55.9731, 10.1531] },
  { name: "Hadsten", pos: [56.3278, 10.0494] },
  { name: "Hornslet", pos: [56.3167, 10.3167] },
  { name: "Hinnerup", pos: [56.2653, 10.0628] },
  { name: "Ry", pos: [56.0897, 9.7647] },
];

const ServiceAreaMap = () => {
  const mapRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapRef.current._leaflet_id) return;
    const map = L.map(mapRef.current, {
      center: [56.14, 10.15],
      zoom: 9,
      scrollWheelZoom: false,
      attributionControl: false,
      zoomControl: true,
    });
    // Free OpenStreetMap tiles (no API key needed), CSS-inverted to a dark theme
    // — CartoDB's dark_all tiles now require a paid API key and show a watermark.
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      className: "dark-map-tiles",
    }).addTo(map);

    L.circle(AARHUS, {
      radius: 50000,
      color: "#00F0FF",
      weight: 2,
      fillColor: "#00F0FF",
      fillOpacity: 0.08,
      dashArray: "6 8",
    }).addTo(map);

    TOWNS.forEach((t) => {
      L.circleMarker(t.pos, {
        radius: t.main ? 8 : 5,
        color: "#0A0A0A",
        weight: 2,
        fillColor: t.main ? "#00F0FF" : "#1E5EFF",
        fillOpacity: 1,
      })
        .addTo(map)
        .bindTooltip(t.name, {
          permanent: true,
          direction: "right",
          offset: [8, 0],
          className: "town-tooltip",
        });
    });

    return () => map.remove();
  }, []);

  return (
    <section className="py-8 md:py-12 bg-black reveal-on-scroll" data-testid="service-area-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-[#00F0FF] text-xs font-bold tracking-[0.3em] uppercase mb-5">Serviceområde</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white tracking-tight mb-6">
              Vi kører til dig — <span className="text-[#00F0FF] text-glow-cyan">hele Østjylland</span>
            </h2>
            <p className="text-[#A0AEC0] leading-relaxed mb-6">
              Med base i Aarhus dækker vores fuldt udstyrede servicevogn hele Østjylland.
              Du behøver aldrig at finde et værksted i nærheden — værkstedet finder dig.
            </p>
            <div className="flex flex-wrap gap-2 mb-6" data-testid="service-area-towns">
              {TOWNS.map((t) => (
                <span
                  key={t.name}
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border ${
                    t.main
                      ? "bg-[#00F0FF] text-black border-[#00F0FF]"
                      : "bg-white/[0.04] text-[#A0AEC0] border-white/10"
                  }`}
                  style={{ fontFamily: 'Saira' }}
                >
                  <MapPin className="w-3 h-3" /> {t.name}
                </span>
              ))}
            </div>
            <ul className="space-y-2.5 mb-8">
              {[
                "Ingen kørselsgebyr — uanset hvor i Østjylland du bor",
                "Åbent hver dag 8-21 — også i weekenden",
                "Dag-til-dag tider: book i dag, vi kommer i morgen",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <span className="text-[#E7E7EA] text-sm font-medium">{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/book">
              <Button className="bg-[#00F0FF] hover:bg-[#00D0E0] text-black font-bold px-8 py-5 rounded-full glow-cyan" data-testid="service-area-book-btn">
                Book i dit område <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div
            ref={mapRef}
            className="h-[360px] md:h-[480px] rounded-sm overflow-hidden border border-white/10 z-0"
            data-testid="service-area-map"
          />
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaMap;
